import {
  ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit, TemplateRef, inject, ElementRef, viewChild, effect,
  AfterViewChecked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TreeService } from './tree.service';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { FlatTreeHelper } from './helpers/flat-tree.helper';
import { FlatTreeModel } from './models';
import { distinctUntilChanged, filter, map, Observable, Subject, take } from 'rxjs';
import { DropZoneOptions, TreeDragDropService, treeNodeBaseClass } from './tree-drag-drop.service';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatRadioButton } from '@angular/material/radio';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';

@Component({
    selector: 'tm-tree',
    templateUrl: './tree.component.html',
    styleUrls: ['./tree.component.css'],
    host: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '(window:resize)': 'onResize()',
    },
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CdkVirtualScrollViewport,
        CdkFixedSizeVirtualScroll,
        CdkVirtualForOf,
        MatCheckbox,
        MatRadioButton,
        MatIconButton,
        MatIcon,
        MatProgressSpinner,
        NgTemplateOutlet,
        AsyncPipe,
    ],
})
export class TreeComponent implements OnInit, OnDestroy, AfterViewChecked {
  private treeService = inject(TreeService);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private treeDragDropService = inject(TreeDragDropService, { optional: true });

  public onResize() {
    this.updateDropzoneHeight();
  }

  @Input()
  public treeNodeTemplate?: TemplateRef<any>;

  @Input()
  public hideRootCollapseArrow = false;

  @Input()
  public getDropZones?: (defaultTarget: HTMLElement, node?: FlatTreeModel, dragNodeIds?: string[]) => DropZoneOptions[];

  @Input()
  public set scrollToItem(scrollToItem: string | undefined | null) {
    if (!scrollToItem) {
      return;
    }
    this.treeService.getTreeDataSource$()
      .pipe(
        filter(dataSource => !!dataSource && dataSource.length > 0),
        debounceTime(50),
        take(1),
      )
      .subscribe(dataSource => {
        const idx = dataSource.findIndex(n => n.id === scrollToItem);
        this.treeElement()?.scrollToIndex(idx, 'smooth');
      });
  }

  @Input()
  public useRadioInputs?: boolean;

  @Input()
  public expandOnGroupClick?: boolean;

  @Input()
  public dragHandleSelector?: string;

  @Input()
  public extendedDropzone?: boolean;

  @Input()
  public openInfoInTree = false;

  @Input()
  public nodeInfoInTreeTemplate?: TemplateRef<any>;

  @Input()
  public activeInTreeInfoNodes: string[] = [];

  @Input()
  public onNodeDoubleClick?: (node: FlatTreeModel) => void;

  @Input()
  public treeLabel?: string;

  private treeElement = viewChild('treeElement', { read: CdkVirtualScrollViewport });

  private extendedDropzoneEl = viewChild('extendedDropzone', { read: ElementRef });

  public selectedNodeId: string | undefined;
  public multiSelectedNodeIds: string[] = [];

  public treeDragDropServiceEnabled = false;

  public readOnlyMode = false;

  private checkedRadioNode: FlatTreeModel | undefined;

  private destroyed = new Subject();

  public extendedDropzoneClass: string = TreeDragDropService.EXTENDED_DROPZONE_CLASS;

  public dataSource$ = this.treeService.getTreeDataSource$();

  private prevTreeHeight = 0;

  constructor() {
    effect(() => {
      const treeElement = this.treeElement();
      if (treeElement) {
        this.updateDropzoneHeight();
      }
    });
  }

  public ngAfterViewChecked(): void {
    const treeHeight = this.treeElement()?.elementRef.nativeElement.offsetHeight || 0;
    if (this.prevTreeHeight !== treeHeight) {
      // Keep previous height to avoid calling checkViewportSize too often
      this.prevTreeHeight = treeHeight;
      this.treeElement()?.checkViewportSize();
    }
  }

  public ngOnInit(): void {
    this.treeService.selectedNode$
      .pipe(
        takeUntil(this.destroyed),
        distinctUntilChanged(),
      )
      .subscribe(selectedNodeId => {
        this.selectedNodeId = selectedNodeId;
        this.cdr.detectChanges();
      });
    this.treeService.multiSelectedNodeIds$
      .pipe(takeUntil(this.destroyed))
      .subscribe(multiSelectedNodeIds => {
        this.multiSelectedNodeIds = multiSelectedNodeIds;
        this.cdr.detectChanges();
      });
    this.treeService.readonlyMode$
      .pipe(takeUntil(this.destroyed))
      .subscribe(readOnlyMode => {
        this.readOnlyMode = readOnlyMode;
        this.treeDragDropServiceEnabled = false;
      });
    if (this.treeDragDropService) {
      this.treeDragDropService.treeDragDropEnabled$
        .pipe(takeUntil(this.destroyed))
        .subscribe(enabled => this.treeDragDropServiceEnabled = enabled);
      this.treeService.getTreeDataSource$()
        .pipe(takeUntil(this.destroyed), filter(dataSource => !!dataSource && dataSource.length > 0))
        .subscribe(() => {
          const el = this.treeElement()?.elementRef.nativeElement;
          this.updateDropzoneHeight();
          if (!el || !this.getDropZones) {
            return;
          }
          this.treeDragDropService?.dataSourceChanged(this.getDropZones(el));
        });
    }
  }

  public hasChild(nodeData: FlatTreeModel) {
    return FlatTreeHelper.isExpandable(nodeData);
  }

  public isExpanded(node: FlatTreeModel) {
    return node.expanded;
  }

  public toggleGroupChecked(node: FlatTreeModel): void {
    if (this.readOnlyMode) {
      return;
    }
    this.toggleNodeChecked(node, this.treeService.getDescendants(node));
  }

  public toggleLeafChecked(node: FlatTreeModel): void {
    if (this.readOnlyMode) {
      return;
    }
    this.toggleNodeChecked(node);
  }

  public setNodeSelected(node: FlatTreeModel, $event?: MouseEvent) {
    if (this.openInfoInTree) {
      return;
    }
    if ($event && ($event.ctrlKey || $event.metaKey)) {
      this.treeService.toggleMultiSelectedNodeId(node.id);
    } else if ($event && $event.shiftKey) {
      this.treeService.selectRangeOfNodes(node.id);
    } else {
      this.treeService.selectionStateChanged(node);
      this.treeService.clearMultiSelectedNodeIds();
    }
    if (this.expandOnGroupClick && FlatTreeHelper.isExpandable(node)) {
      this.treeService.toggleNodeExpanded(node);
    }
  }

  public toggleNodeExpansion($event: MouseEvent, node: FlatTreeModel) {
    $event.stopPropagation();
    this.treeService.toggleNodeExpanded(node);
  }

  public getNodeClassName(node: FlatTreeModel) {
    const cls = [
      treeNodeBaseClass,
      FlatTreeHelper.isExpandable(node) ? `${treeNodeBaseClass}--folder` : `${treeNodeBaseClass}--leaf`,
      `${treeNodeBaseClass}--level-${FlatTreeHelper.getLevel(node)}`,
      `${treeNodeBaseClass}--${!node.checkbox ? 'no-' : ''}checkbox`,
    ];
    if (node.className) {
      cls.push(node.className);
    }
    return cls.join(' ');
  }

  public isIndeterminate(node: FlatTreeModel) {
    return this.treeService.isIndeterminate(node);
  }

  public isChecked(node: FlatTreeModel) {
    return this.treeService.isChecked(node);
  }

  public ngOnDestroy() {
    this.destroyed.next(null);
    this.destroyed.complete();
  }

  private toggleNodeChecked(node: FlatTreeModel, descendants?: FlatTreeModel[]) {
    const stateChange: FlatTreeModel[] = [];
    const checked = descendants ? !this.treeService.descendantsAllSelected(node) : !this.treeService.isChecked(node);
    stateChange.push({ ...node, checked });
    if (descendants) {
      stateChange.push(...descendants.map(d => ({ ...d, checked })));
    }
    this.treeService.checkStateChanged(stateChange);
  }

  public handleDragStart(event: DragEvent, node: FlatTreeModel) {
    const treeElement = this.treeElement();
    if (!this.treeDragDropService || !this.getDropZones || !treeElement) {
      event.preventDefault();
      return;
    }
    const dragNodeIds = this.multiSelectedNodeIds.includes(node.id)
      ? this.multiSelectedNodeIds.filter(nodeId => this.treeService.hasNode(nodeId))
      : [node.id];
    const dropZoneConfig = this.getDropZones(treeElement.elementRef.nativeElement, node, dragNodeIds);
    const dragAllowed = dragNodeIds.every(nodeId => dropZoneConfig.some(dz => dz.dragAllowed ? dz.dragAllowed(nodeId) : true));
    if (!dragAllowed) {
      event.preventDefault();
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.treeDragDropService?.handleDragStart(event, node, dropZoneConfig, dragNodeIds);
    });
  }

  public handleRadioChange(node: FlatTreeModel, $event: MouseEvent) {
    $event.stopPropagation();
    const checkChange: FlatTreeModel[] = [];
    if (this.checkedRadioNode) {
      checkChange.push({ ...this.checkedRadioNode, checked: false });
    }
    checkChange.push({ ...node, checked: !node.checked });

    this.treeService.checkStateChanged(checkChange);
    this.checkedRadioNode = node;
  }

  public enableDrag($event: MouseEvent | TouchEvent) {
    if (!$event.target || !this.treeDragDropServiceEnabled) {
      return;
    }
    const treeNode = ($event.target as HTMLElement).closest(`.${treeNodeBaseClass}`);
    if (this.dragHandleSelector && treeNode) {
      const dragHandle = ($event.target as HTMLElement).closest(this.dragHandleSelector);
      if (!dragHandle || !treeNode.contains(dragHandle)) {
        return;
      }
    }
    ($event.target as HTMLElement).closest(`.${treeNodeBaseClass}`)?.setAttribute('draggable', 'true');
  }

  public stopDrag($event: MouseEvent | TouchEvent) {
    if (!$event.target) {
      return;
    }
    ($event.target as HTMLElement).closest(`.${treeNodeBaseClass}`)?.setAttribute('draggable', 'false');
  }

  public treeTrackBy(idx: number, node: FlatTreeModel) {
    return node.id;
  }

  public depth(node: FlatTreeModel) {
    return node.level;
  }

  private updateDropzoneHeight() {
    setTimeout(() => {
      const dropzoneEl = this.extendedDropzoneEl();
      const treeElement = this.treeElement();
      if (!dropzoneEl || !treeElement) {
        return;
      }
      const viewportHeight = treeElement.elementRef.nativeElement.offsetHeight;
      const contentHeight = treeElement.measureRenderedContentSize();
      const dropzoneHeight = Math.max(0, viewportHeight - contentHeight);
      dropzoneEl.nativeElement.style.height = `${dropzoneHeight}px`;
    }, 100);
  }

  public getChildren(node: FlatTreeModel): string[] {
    return this.treeService.getDescendants(node).map(n => n.id);
  }

  public getAllRootNodeIds(): string[] {
    return this.treeService.getAllRootNodeIds();
  }

  public getSetSize$(node: FlatTreeModel): Observable<number> {
    return this.dataSource$.pipe(
      map(nodes => {
        const parentNode = FlatTreeHelper.getParentNode(node, nodes);
        return parentNode ? this.treeService.getDescendants(parentNode).length : this.treeService.getAllRootNodeIds().length;
      }),
    );
  }

  public getPositionInSet$(node: FlatTreeModel): Observable<number> {
    return this.dataSource$.pipe(
      map(nodes => {
        const parentNode = FlatTreeHelper.getParentNode(node, nodes);
        const siblings = parentNode ? this.treeService.getDescendants(parentNode) : nodes;
        return siblings.findIndex(n => n.id === node.id) + 1;
      }),
    );
  }

}
